import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./AuthContext";
import "./index.css";

function StudentLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [classworkCount, setClassworkCount] = useState(0);
  const [homeworkCount, setHomeworkCount] = useState(0);
  const { currentUser } = useAuth();

  const handleLogout = async () => {
    setIsLoggingOut(true);
    navigate("/logging-out");

    setTimeout(async () => {
      try {
        await signOut(getAuth());
        navigate("/");
      } catch (error) {
        console.error("Logout failed:", error);
      }
    }, 2200);
  };

  useEffect(() => {
  const fetchAssignmentCounts = async () => {
    if (!currentUser) return;

    const [homeworkSnap, assignmentSnap] = await Promise.all([
      getDocs(query(collection(db, "homework"), where("studentUID", "==", currentUser.uid))),
      getDocs(collection(db, "assignments")),
    ]);

    const allHomework = homeworkSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const allAssignments = assignmentSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    let flatHomeworkItems = [];

    for (const hw of allHomework) {
      const linkedAssignments = (hw.assignments || []).map(id =>
        allAssignments.find(a => a.id === id)
      ).filter(Boolean);

      if (linkedAssignments.length === 0) {
        flatHomeworkItems.push({
          id: `no-assignment-${hw.id}`,
          noAssignment: true,
          hwId: hw.id
        });
      } else {
        flatHomeworkItems.push(
          ...linkedAssignments.map(a => ({
            ...a,
            hwId: hw.id
          }))
        );
      }
    }

    let outstanding = 0;
    for (const item of flatHomeworkItems) {
      const progressRef = doc(db, "assignmentProgress", `${item.id}_${currentUser.uid}`);
      const snap = await getDoc(progressRef);
      const data = snap.exists() ? snap.data() : {};

      if (!data.handedIn) {
        outstanding += 1;
      }
    }

    setHomeworkCount(outstanding);
  };

  fetchAssignmentCounts();
}, [currentUser]);


  return (
    <div>
      <nav style={styles.navbar}>
        <div style={styles.left}>
          <div style={styles.logo}>
  🎒{currentUser?.displayName || "Student"}'s account
</div>

          <div style={styles.links}>
            <Link to="/student-dashboard" style={styles.link}>🏠 Dashboard</Link>

            <Link to="/classwork-page" style={styles.link}>
              📚 Classwork
              {classworkCount > 0 && (
                <span className="badge pulse" style={{ backgroundColor: "#0984e3" }}>
                  {classworkCount}
                </span>
              )}
            </Link>

            <Link to="/homework-page" style={styles.link}>
              📝 Homework
              {homeworkCount > 0 && (
                <span className="badge pulse" style={{ backgroundColor: "#d63031" }}>
                  {homeworkCount}
                </span>
              )}
            </Link>

            <Link to="/student-resources" style={styles.link}>🧠 Resources</Link>
          </div>
        </div>

        <button onClick={handleLogout} className="logout-btn">🚪 Log Out</button>
      </nav>

      <main>{children}</main>

      <style>
        {`
          a:hover {
            color: #00cec9 !important;
            text-decoration: underline;
            text-underline-offset: 4px;
            transition: color 0.25s ease, text-decoration 0.25s ease;
          }

          .logout-btn {
            background-color: #d63031;
            color: white;
            border: none;
            padding: 0.6rem 1rem;
            border-radius: 8px;
            cursor: pointer;
            font-weight: bold;
            font-size: 0.95rem;
            box-shadow: 0 2px 5px rgba(0,0,0,0.1);
            transition: background-color 0.3s ease;
          }

          .logout-btn:hover {
            background-color: #ff7675;
          }

          .badge {
            margin-left: 6px;
            display: inline-block;
            padding: 2px 8px;
            font-size: 0.7rem;
            font-weight: bold;
            color: #fff;
            border-radius: 999px;
            line-height: 1;
            vertical-align: middle;
          }

          @keyframes pulse {
            0% {
              transform: scale(1);
              box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.1);
            }
            50% {
              transform: scale(1.15);
              box-shadow: 0 0 0 5px rgba(0, 0, 0, 0);
            }
            100% {
              transform: scale(1);
              box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
            }
          }

          .pulse {
            animation: pulse 1.6s infinite;
          }
        `}
      </style>
    </div>
  );
}

const styles = {
  navbar: {
    backgroundColor: "#ffeaa7",
    padding: "1rem 2rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontFamily: "'Fredoka', cursive",
    borderBottom: "2px solid #fdcb6e",
    position: "sticky",
    top: 0,
    zIndex: 1000,
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: "2rem",
  },
  logo: {
    fontSize: "1.6rem",
    fontWeight: "bold",
    color: "#2d3436",
  },
  links: {
    display: "flex",
    gap: "1.4rem",
  },
  link: {
    textDecoration: "none",
    fontSize: "1.1rem",
    fontWeight: "500",
    color: "#2d3436",
    transition: "all 0.3s ease",
    position: "relative",
  },
};

export default StudentLayout;
